import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  Grid,
} from "antd";
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Order, OrderItem } from "../model/types";
import { PATHS } from "../../../app/router/paths";
import { MOCK_ORDERS } from "../../../utils/constants";

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface OrderCreatePageProps {
  isDesktop?: boolean;
  ordersCount?: number;
  onBackToList?: () => void;
  onSubmitOrder?: (order: Order) => void;
}

export const OrderCreatePage: React.FC<OrderCreatePageProps> = ({
  isDesktop: propIsDesktop,
  ordersCount: propOrdersCount,
  onBackToList,
  onSubmitOrder,
}) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isDesktop = propIsDesktop ?? !!screens.md;
  const ordersCount = propOrdersCount ?? MOCK_ORDERS.length;

  const [form] = Form.useForm();
  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    {
      id: "new-1",
      productCode: "PRD-101",
      productName: "ワークシャツ タイプA",
      unitPrice: 2500,
      sizes: { S: 0, M: 0, L: 0, XL: 0, "3L": 0 },
    },
  ]);

  const handleBack = () => {
    if (onBackToList) {
      onBackToList();
    } else {
      navigate(PATHS.ORDERS);
    }
  };

  const calculateTotal = (items: OrderItem[]) => {
    return items.reduce((sum, item) => {
      const totalQty = Object.values(item.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
      return sum + totalQty * item.unitPrice;
    }, 0);
  };

  const handleSizeChange = (
    itemId: string,
    sizeKey: keyof OrderItem["sizes"],
    value: number | null
  ) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            sizes: {
              ...item.sizes,
              [sizeKey]: value || 0,
            },
          };
        }
        return item;
      })
    );
  };

  const handleAddProductRow = () => {
    const newItem: OrderItem = {
      id: `new-${Date.now()}`,
      productCode: "PRD-102",
      productName: "ワークパンツ タイプB",
      unitPrice: 3200,
      sizes: { S: 0, M: 0, L: 0, XL: 0, "3L": 0 },
    };
    setOrderItems([...orderItems, newItem]);
  };

  const handleRemoveProductRow = (id: string) => {
    setOrderItems(orderItems.filter((item) => item.id !== id));
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      const newOrder: Order = {
        id: String(ordersCount + 1),
        orderNo: `ORD-2026-${String(ordersCount + 1).padStart(3, "0")}`,
        customerName: values.customerName || "メイセイ店舗 A",
        orderDate: dayjs().format("YYYY-MM-DD"),
        deliveryDate: values.deliveryDate ? dayjs(values.deliveryDate).format("YYYY-MM-DD") : "",
        status: "pending",
        totalAmount: calculateTotal(orderItems),
        items: orderItems,
        notes: values.notes,
      };
      if (onSubmitOrder) {
        onSubmitOrder(newOrder);
      } else {
        navigate(PATHS.ORDER_CONFIRM, { state: { order: newOrder } });
      }
    });
  };

  return (
    <div style={{ padding: isDesktop ? "16px" : "12px", maxWidth: 1200, margin: "0 auto", minHeight: "100vh" }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack} />
            <span>新規注文入力</span>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 10 }}
      >
        <Form form={form} layout="vertical" initialValues={{ customerName: "メイセイ店舗 A" }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="店舗・顧客名"
                name="customerName"
                rules={[{ required: true, message: "店舗名を選択してください" }]}
              >
                <Select
                  options={[
                    { value: "メイセイ店舗 A", label: "メイセイ店舗 A" },
                    { value: "メイセイ店舗 B", label: "メイセイ店舗 B" },
                    { value: "メイセイ店舗 C", label: "メイセイ店舗 C" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="納品希望日"
                name="deliveryDate"
                rules={[{ required: true, message: "納品希望日を選択してください" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="備考・配送指定" name="notes">
            <Input.TextArea rows={2} placeholder="特記事項があれば入力してください" />
          </Form.Item>
        </Form>
      </Card>

      {/* DUAL UI: Desktop Matrix Table vs Mobile Touch Cards */}
      <Card title="商品・サイズ別数量入力" style={{ borderRadius: 10 }}>
        {isDesktop ? (
          <div style={{ overflowX: "auto", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>商品</th>
                  <th style={{ padding: 8, width: 90 }}>単価</th>
                  <th style={{ padding: 8, width: 70 }}>S</th>
                  <th style={{ padding: 8, width: 70 }}>M</th>
                  <th style={{ padding: 8, width: 70 }}>L</th>
                  <th style={{ padding: 8, width: 70 }}>XL</th>
                  <th style={{ padding: 8, width: 70 }}>3L</th>
                  <th style={{ padding: 8, width: 90 }}>小計</th>
                  <th style={{ padding: 8, width: 60 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item) => {
                  const rowQty = Object.values(item.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
                  const rowSubtotal = rowQty * item.unitPrice;
                  return (
                    <tr key={item.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: 8, textAlign: "left" }}>
                        <Select
                          style={{ width: "100%" }}
                          value={item.productCode || undefined}
                          placeholder="商品を選択"
                          onChange={(val) => {
                            const sampleProducts: Record<string, { name: string; price: number }> = {
                              "PRD-101": { name: "ワークシャツ タイプA", price: 2500 },
                              "PRD-102": { name: "カーゴパンツ タイプB", price: 3800 },
                              "PRD-201": { name: "防寒ブルゾン", price: 6000 },
                            };
                            const selected = sampleProducts[val];
                            setOrderItems((prev) =>
                              prev.map((i) =>
                                i.id === item.id
                                  ? {
                                    ...i,
                                    productCode: val,
                                    productName: selected.name,
                                    unitPrice: selected.price,
                                  }
                                  : i
                              )
                            );
                          }}
                          options={[
                            { value: "PRD-101", label: "PRD-101: ワークシャツ タイプA (¥2,500)" },
                            { value: "PRD-102", label: "PRD-102: カーゴパンツ タイプB (¥3,800)" },
                            { value: "PRD-201", label: "PRD-201: 防寒ブルゾン (¥6,000)" },
                          ]}
                        />
                      </td>
                      <td style={{ padding: 8 }}>¥{item.unitPrice.toLocaleString()}</td>
                      {(["S", "M", "L", "XL", "3L"] as const).map((sz) => (
                        <td style={{ padding: 4 }} key={sz}>
                          <InputNumber
                            min={0}
                            max={999}
                            style={{ width: "100%" }}
                            value={item.sizes[sz]}
                            onChange={(val) => handleSizeChange(item.id, sz, val)}
                          />
                        </td>
                      ))}
                      <td style={{ padding: 8, fontWeight: "bold" }}>¥{rowSubtotal.toLocaleString()}</td>
                      <td style={{ padding: 8 }}>
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveProductRow(item.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Space orientation="vertical" style={{ width: "100%", marginBottom: 16 }} size="large">
            {orderItems.map((item, index) => {
              const rowQty = Object.values(item.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
              const rowSubtotal = rowQty * item.unitPrice;
              return (
                <Card
                  key={item.id}
                  type="inner"
                  title={`商品 #${index + 1}`}
                  extra={
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveProductRow(item.id)}
                    />
                  }
                  style={{ borderRadius: 8, padding: 12 }}
                >
                  <Form.Item label="商品選択" style={{ marginBottom: 12 }}>
                    <Select
                      style={{ width: "100%" }}
                      value={item.productCode || undefined}
                      placeholder="商品を選択"
                      onChange={(val) => {
                        const sampleProducts: Record<string, { name: string; price: number }> = {
                          "PRD-101": { name: "ワークシャツ タイプA", price: 2500 },
                          "PRD-102": { name: "カーゴパンツ タイプB", price: 3800 },
                          "PRD-201": { name: "防寒ブルゾン", price: 6000 },
                        };
                        const selected = sampleProducts[val];
                        setOrderItems((prev) =>
                          prev.map((i) =>
                            i.id === item.id
                              ? {
                                ...i,
                                productCode: val,
                                productName: selected.name,
                                unitPrice: selected.price,
                              }
                              : i
                          )
                        );
                      }}
                      options={[
                        { value: "PRD-101", label: "PRD-101: ワークシャツ タイプA (¥2,500)" },
                        { value: "PRD-102", label: "PRD-102: カーゴパンツ タイプB (¥3,800)" },
                        { value: "PRD-201", label: "PRD-201: 防寒ブルゾン (¥6,000)" },
                      ]}
                    />
                  </Form.Item>

                  <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                    サイズ別数量入力:
                  </Text>
                  <Row gutter={[8, 8]}>
                    {(["S", "M", "L", "XL", "3L"] as const).map((sz) => (
                      <Col span={8} key={sz}>
                        <div style={{ background: "#f5f5f5", padding: "6px", borderRadius: 6, textAlign: "center" }}>
                          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                            {sz}
                          </Text>
                          <InputNumber
                            min={0}
                            max={999}
                            size="small"
                            style={{ width: "100%", marginTop: 2 }}
                            value={item.sizes[sz]}
                            onChange={(val) => handleSizeChange(item.id, sz, val)}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 8,
                      borderTop: "1px dashed #e8e8e8",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      小計 (数量: {rowQty})
                    </Text>
                    <Text strong style={{ fontSize: 15, color: "#1677ff" }}>
                      ¥{rowSubtotal.toLocaleString()}
                    </Text>
                  </div>
                </Card>
              );
            })}
          </Space>
        )}

        <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddProductRow}>
            商品行を追加
          </Button>
          <div style={{ textAlign: "right" }}>
            <Text strong style={{ fontSize: isDesktop ? 16 : 14, display: "block" }}>
              合計金額: ¥{calculateTotal(orderItems).toLocaleString()}
            </Text>
            <Button
              type="primary"
              size={isDesktop ? "large" : "middle"}
              onClick={handleFormSubmit}
              style={{ marginTop: 8 }}
            >
              注文確認へ進む
            </Button>
          </div>
        </Row>
      </Card>
    </div>
  );
};
